import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaChevronDown, FaChevronUp, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';

const roles = [
  {
    id: 1,
    title: 'Senior Laravel Developer',
    company: 'Nitwebs',
    location: 'Ludhiana, Punjab, India',
    duration: 'May 2024 - Aug 2025',
    highlights: [
      'Architected SaaS platform supporting thousands of concurrent users with queue-driven workflows.',
      'Optimized MySQL and caching layers, reducing load times by 60% on high-traffic modules.',
      'Designed CI/CD workflows and mentored junior developers on clean Laravel practices.',
    ],
    tech: ['Laravel', 'Redis', 'MySQL', 'AWS S3'],
  },
  {
    id: 2,
    title: 'Senior Laravel Developer',
    company: 'Code Rumble',
    location: 'Ludhiana, Punjab, India',
    duration: 'March 2021 - April 2024',
    highlights: [
      'Led backend refactoring from monolithic structure to modular Laravel services.',
      'Implemented Redis caching and query optimization to improve response time and throughput.',
      'Built deployment pipelines with GitHub Actions for automated production releases.',
    ],
    tech: ['Laravel', 'Vue.js', 'Redis', 'GitHub Actions'],
  },
  {
    id: 3,
    title: 'PHP Developer',
    company: 'Technocrats Horizons',
    location: 'Ludhiana, Punjab, India',
    duration: 'March 2018 - May 2020',
    highlights: [
      'Built enterprise and e-commerce systems with Laravel and WordPress.',
      'Migrated legacy codebases into cleaner, maintainable architecture patterns.',
      'Integrated payment gateways and managed reliable deployment workflows.',
    ],
    tech: ['PHP', 'Laravel', 'WordPress', 'MySQL'],
  },
];

const ExperienceV2 = () => {
  const [expandedId, setExpandedId] = useState(1);

  const toggleCard = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 tracking-tight text-gray-900">Experience</h2>
        <div className="w-16 h-1 bg-blue-600 rounded mt-2 mb-6"></div>
        <div className="space-y-12">
          {roles.map((role, i) => (
            <motion.div
              key={role.id}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              viewport={{ once: true }}
              className="rounded-xl border border-gray-200 border-l-4 border-l-blue-500 bg-gray-50 p-6 md:p-8 md:pl-6 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 space-y-3"
            >
              <button
                type="button"
                onClick={() => toggleCard(role.id)}
                className="w-full text-left flex items-center justify-between gap-4"
              >
                <div>
                  <p className="text-lg md:text-xl font-semibold text-gray-900">{role.title}</p>
                  <p className="text-blue-600 font-medium">{role.company}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-4 text-sm md:text-base text-gray-600 leading-relaxed">
                    <span className="inline-flex items-center gap-2">
                      <FaCalendarAlt className="text-blue-600" />
                      {role.duration}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaMapMarkerAlt className="text-blue-600" />
                      {role.location}
                    </span>
                  </div>
                </div>
                <span className="text-blue-600">
                  {expandedId === role.id ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </button>

              <AnimatePresence initial={false}>
                {expandedId === role.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 mt-4 border-t border-gray-200 space-y-3">
                      <ul className="space-y-2">
                        {role.highlights.map((item) => (
                          <li key={item} className="max-w-prose text-sm md:text-base text-gray-600 leading-relaxed pl-1">- {item}</li>
                        ))}
                      </ul>
                      <div className="flex flex-wrap gap-2">
                        {role.tech.map((tech) => (
                          <span
                            key={tech}
                            className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExperienceV2;
